import React from 'react';
import { Copy, Car } from 'lucide-react';
import { NgapAct } from '../../types/ngap';
import { calculateTarif, formatTarif } from '../../utils/pricingUtils';
import { useToast } from '../../hooks/useToast';
import { logger } from '../../utils/logger';

interface ActPricingProps {
  act: NgapAct;
  darkMode: boolean;
  activeCotationValue: string;
}

const ActPricing: React.FC<ActPricingProps> = ({
  act,
  darkMode,
  activeCotationValue
}) => {
  const { success } = useToast();

  const tarifMetropole = React.useMemo(() =>
    formatTarif(calculateTarif(activeCotationValue, false, act.cotation)),
    [activeCotationValue, act.cotation]
  );

  const tarifDrom = React.useMemo(() =>
    formatTarif(calculateTarif(activeCotationValue, true, act.cotation)),
    [activeCotationValue, act.cotation]
  );

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      success('Copié !', `${text}€ copié dans le presse-papiers`);
    } catch (err) {
      logger.error('Erreur lors de la copie:', err);
    }
  };

  const labelColor = darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65';
  const valueColor = darkMode ? 'text-appines-white' : 'text-appines-black';
  const dividerColor = darkMode ? 'bg-appines-white/12' : 'bg-[rgba(1,42,54,0.12)]';
  const copyBtnColor = darkMode
    ? 'text-appines-white/35 hover:text-appines-white'
    : 'text-appines-petrol/35 hover:text-appines-petrol';

  return (
    <div
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-sm ${
        darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'
      }`}
    >
      <div className="flex items-center gap-1.5" role="group" aria-label="Tarif métropole">
        <span className={labelColor}>Métropole</span>
        <span className={`font-semibold ${valueColor}`}>{tarifMetropole} €</span>
        <button
          onClick={() => copyToClipboard(tarifMetropole)}
          className={`p-1 rounded transition-colors ${copyBtnColor}`}
          aria-label={`Copier le tarif métropole ${tarifMetropole} euros`}
        >
          <Copy className="h-3 w-3" />
        </button>
      </div>

      <span className={`hidden sm:inline-block w-px h-4 ${dividerColor}`} aria-hidden="true" />

      <div className="flex items-center gap-1.5" role="group" aria-label="Tarif DROM">
        <span className={labelColor}>DROM</span>
        <span className={`font-semibold ${valueColor}`}>{tarifDrom} €</span>
        <button
          onClick={() => copyToClipboard(tarifDrom)}
          className={`p-1 rounded transition-colors ${copyBtnColor}`}
          aria-label={`Copier le tarif DROM ${tarifDrom} euros`}
        >
          <Copy className="h-3 w-3" />
        </button>
      </div>

      <span className={`hidden sm:inline-block w-px h-4 ${dividerColor}`} aria-hidden="true" />

      <div className="flex items-center gap-1.5" role="group" aria-label="Indemnité de déplacement">
        <Car className={`h-3.5 w-3.5 ${labelColor}`} />
        <span className={`font-medium ${valueColor}`}>
          {act.indemnite_deplacement === 'IFS' ? 'IFS 4 €' : 'IFD 2,5 €'}
        </span>
      </div>
    </div>
  );
};

export default ActPricing;
